/** Shared visual tokens for IPD form fields — derived from ipdColors. */

import { IPD_COLORS, IPD_SHAPE } from "./ipdColors";

export const IPD_FIELD_COLORS = {
  label: IPD_COLORS.textPrimary,
  border: IPD_COLORS.border,
  borderHover: IPD_COLORS.borderHover,
  borderFocus: IPD_COLORS.primary,
  bg: IPD_COLORS.cardBg,
  bgDisabled: IPD_COLORS.pageBg,
  placeholder: IPD_COLORS.textMuted,
  required: IPD_COLORS.error.text,
  shadow: IPD_COLORS.shadow.field,
  shadowHover: IPD_COLORS.shadow.fieldHover,
  shadowFocus: IPD_COLORS.shadow.fieldFocus,
  menuShadow: IPD_COLORS.shadow.menu,
};

/** Uniform control height for text, select, and date fields. */
export const IPD_FIELD_HEIGHT = 40;

const ipdInputPadding = {
  padding: "0 14px",
  height: "100%",
  minHeight: "unset",
  fontSize: "0.875rem",
  lineHeight: 1.43,
  boxSizing: "border-box",
};

/** Semi-bold label sitting above the field (not floating inside). */
export const ipdLabelSx = {
  position: "relative",
  transform: "none",
  mb: 0.75,
  fontSize: "0.8125rem",
  fontWeight: 600,
  lineHeight: 1.4,
  color: IPD_FIELD_COLORS.label,
  "&.Mui-focused": { color: IPD_FIELD_COLORS.label },
  "& .MuiFormLabel-asterisk": { color: IPD_FIELD_COLORS.required },
};

/** Elevated outlined input — matches admission screenshot styling. */
export const ipdElevatedInputSx = {
  "& .MuiOutlinedInput-root": {
    bgcolor: IPD_FIELD_COLORS.bg,
    borderRadius: `${IPD_SHAPE.radiusLg}px`,
    minHeight: IPD_FIELD_HEIGHT,
    height: IPD_FIELD_HEIGHT,
    boxSizing: "border-box",
    boxShadow: IPD_FIELD_COLORS.shadow,
    transition: "box-shadow 0.2s ease, border-color 0.2s ease",
    "& fieldset": {
      borderColor: IPD_FIELD_COLORS.border,
      borderWidth: "1px",
    },
    "&:hover": {
      boxShadow: IPD_FIELD_COLORS.shadowHover,
      "& fieldset": { borderColor: IPD_FIELD_COLORS.borderHover },
    },
    "&.Mui-focused": {
      boxShadow: IPD_FIELD_COLORS.shadowFocus,
      "& fieldset": {
        borderColor: IPD_FIELD_COLORS.borderFocus,
        borderWidth: "1px",
      },
    },
    "&.Mui-disabled": {
      bgcolor: IPD_FIELD_COLORS.bgDisabled,
      boxShadow: "none",
    },
    "& input::placeholder, & textarea::placeholder": {
      color: IPD_FIELD_COLORS.placeholder,
      opacity: 1,
    },
  },
  "& .MuiOutlinedInput-root.MuiInputBase-multiline": {
    height: "auto",
    minHeight: IPD_FIELD_HEIGHT,
  },
  "& .MuiOutlinedInput-input": {
    ...ipdInputPadding,
  },
  "& .MuiSelect-select": {
    ...ipdInputPadding,
    display: "flex",
    alignItems: "center",
    paddingRight: "32px !important",
  },
};

export function mergeIpdFieldSx(userSx) {
  if (!userSx) return ipdElevatedInputSx;
  return Array.isArray(userSx)
    ? [ipdElevatedInputSx, ...userSx]
    : [ipdElevatedInputSx, userSx];
}

/**
 * Select menu props: opens below by default, flips above when needed,
 * and keeps a gap so the list never covers the field.
 */
export function getIpdSelectMenuProps(customMenuProps = {}) {
  const {
    PaperProps: customPaper = {},
    PopperProps: customPopper = {},
    ...rest
  } = customMenuProps;

  const customModifiers = customPopper.modifiers || [];
  const MENU_GAP = 8;

  return {
    ...rest,
    marginThreshold: rest.marginThreshold ?? MENU_GAP,
    PaperProps: {
      elevation: 3,
      ...customPaper,
      sx: {
        borderRadius: `${IPD_SHAPE.radiusLg}px`,
        maxHeight: 280,
        boxShadow: IPD_FIELD_COLORS.menuShadow,
        ...(customPaper.sx || {}),
      },
    },
    PopperProps: {
      ...customPopper,
      placement: customPopper.placement ?? "bottom-start",
      modifiers: [
        {
          name: "offset",
          options: {
            offset: ({ placement }) => {
              const isTop = placement.startsWith("top");
              return [0, isTop ? -MENU_GAP : MENU_GAP];
            },
          },
        },
        {
          name: "flip",
          enabled: true,
          options: {
            fallbackPlacements: ["bottom-start", "top-start"],
            padding: MENU_GAP,
            altBoundary: true,
            rootBoundary: "viewport",
          },
        },
        {
          name: "preventOverflow",
          enabled: true,
          options: {
            padding: MENU_GAP,
            altAxis: true,
            altBoundary: true,
            tether: true,
            rootBoundary: "viewport",
          },
        },
        ...customModifiers,
      ],
    },
  };
}
